<template>
  <base-form :title="title"
             :loading="submitLoading"
             @submit="submitForm">
    <template v-slot:content>
      <div class="-mx-3">
        <dl>
          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('generic.name')}}
            </dt>
            <dd class="list-row-dd">
              {{model.name || $t('generic.na')}}
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('generic.type')}}
            </dt>
            <dd class="list-row-dd">
              {{model.type || $t('generic.na')}}
            </dd>
          </div>
          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('thirdParties.active')}}
            </dt>
            <dd class="list-row-dd">
              <status-badge :status="model.active ? 'active': 'inactive'"/>
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('thirdParties.enabled')}}
            </dt>
            <dd class="list-row-dd">
              <fg-input :name="$t('thirdParties.enabled')"
                        v-model="model.enabled">
                <base-switch :value="model.enabled"
                             @input="onStatusToggle">
                </base-switch>
              </fg-input>
            </dd>
          </div>

          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('generic.createdAt')}}
            </dt>
            <dd class="list-row-dd">
              {{$formatDate(model.created_at, FRIENDLY_DATETIME_FORMAT)}}
            </dd>
          </div>
        </dl>
      </div>
    </template>
    <template v-if="hasPublicFields">
      <section-separator has-border size="lg">
        {{$t('thirdParties.publicInformation')}}
      </section-separator>

      <template v-for="(field, fieldName) in model.fields.public">
        <fg-input v-if="field.type !== 'array' && !fieldName.includes('*')"
                  :key="fieldName"
                  :label="$t(`thirdParties.${fieldName}`)"
                  :name="$t(`thirdParties.${fieldName}`)"
                  :error="getError($t(`thirdParties.${fieldName}`))"
                  :required="field.validation === 'required'"
                  v-model="model.public_information[fieldName]"
                  v-validate="`${getFieldValidation(field.validation)}`">
          <base-switch v-if="field.type === 'boolean'"
                       v-model="model.public_information[fieldName]">
          </base-switch>
        </fg-input>
      </template>
    </template>
    <template v-if="model.name === 'Mailchimp'">
      <data-table :data="get(model, 'public_information.lists', [])"
                  :title="$t('thirdParties.newsletterLists')"
                  :columns="listColumns"
                  :allow-api-delete="false"
                  :disable-permission-checks="true"
                  :go-to-add-on-click="false"
                  :go-to-details-on-row-click="false"
                  operations="add,delete"
                  @on-add="showListModal = true"
                  @on-delete="submitForm"
      >
        <template v-slot:id="{row}">
          {{getNewsLetterName(row.id).name}}
        </template>
        <template v-slot:list_id="{row}">
          {{getListName(row.list_id)}}
        </template>
      </data-table>
      <modal :show.sync="showListModal"
             :title="$t('thirdParties.newsletterLists.add')"
      >
        <form @submit.prevent="addToList">

          <fg-input :label="$t(`thirdParties.newsletter`)"
                    class="mr-4 w-full"
          >
            <el-select v-model="listModel.id"
                       class="select-default w-full"
            >
              <el-option v-for="newsletter in newsletters"
                         :label="newsletter.name"
                         :value="newsletter.id"
                         :key="newsletter.id"
                         class="select-default"
              >
              </el-option>
            </el-select>
          </fg-input>

          <fg-input :label="$t(`thirdParties.mailchimpList`)"
                    class="w-full mr-4">
            <el-select v-model="listModel.list_id"
                       class="select-default w-full"
            >
              <el-option v-for="(value, id) in get(model, 'data.lists')"
                         :label="value"
                         :value="id"
                         :key="id"
                         class="select-default"
              >
              </el-option>
            </el-select>
          </fg-input>

          <div class="flex w-full justify-center">
            <submit-button native-type="submit"></submit-button>
          </div>
        </form>
      </modal>
    </template>

    <template v-if="hasPrivateFields">
      <section-separator has-border size="lg">
        {{$t('thirdParties.privateInformation')}}
      </section-separator>

      <fg-input v-for="(field, fieldName)  in model.fields.private"
                :key="fieldName"
                :label="$t(`thirdParties.${fieldName}`)"
                :name="$t(`thirdParties.${fieldName}`)"
                :error="getError($t(`thirdParties.${fieldName}`))"
                v-model="model.private_information[fieldName]"
                v-validate="`${getFieldValidation(field.validation)}`">
        <base-switch v-if="field.type === 'boolean'"
                     v-model="model.private_information[fieldName]">
        </base-switch>
      </fg-input>
    </template>
  </base-form>
</template>
<script>
  import get from 'lodash/get'
  import { Select, Option } from 'element-ui'
  import ThirdPartiesService from 'src/services/ThirdPartiesService'
  import NewsLetterService from 'src/services/NewsLetterService'
  import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
  import { FRIENDLY_DATETIME_FORMAT } from 'src/config/dateUtils'

  export default {
    components: {
      TooltipIconButton,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    props: {
      data: {
        type: Object,
        default: () => ({})
      },
      title: String,
      submitLoading: Boolean,
    },
    data () {
      return {
        FRIENDLY_DATETIME_FORMAT,
        showListModal: false,
        model: {
          fields: {
            public: [],
            private: [],
          },
          private_information: {},
          public_information: {},
        },
        listModel: {
          id: '',
          list_id: '',
        },
        newsletters: [],
        listColumns: [
          {
            prop: 'id',
            label: this.$t('thirdParties.newsletter'),
            minWidth: '100px',
            sortable: true
          },
          {
            prop: 'list_id',
            label: this.$t('thirdParties.mailchimpList'),
            minWidth: '100px',
            sortable: true
          }
        ]
      }
    },
    computed: {
      hasPublicFields () {
        const fields = this.model.fields.public || []
        const publicInfo = this.model.public_information || {}
        return Object.keys(publicInfo).length > 0 && Object.keys(fields).length > 0
      },
      hasPrivateFields () {
        const fields = this.model.fields.private || []
        const privateInfo = this.model.private_information || {}
        return Object.keys(privateInfo).length > 0 && Object.keys(fields).length > 0
      }
    },
    methods: {
      get,
      async submitForm () {
        let isFormValid = await this.$validator.validateAll()
        if (!isFormValid) {
          return
        }
        this.$emit('submit', this.model)
      },
      async onStatusToggle (value) {
        await ThirdPartiesService.toggleStatus(this.$route.params.id, {
          enabled: value
        })
        this.model.enabled = value
      },
      getFieldValidation (validation) {
        if (validation === 'required') {
          return 'required'
        }
        return ''
      },
      addToList () {
        if (!this.model.public_information) {
          this.model.public_information = {
            lists: []
          }
        }
        if (!this.model.public_information.lists) {
          this.model.public_information.lists = []
        }
        this.model.public_information.lists.push(this.listModel)
        this.listModel = {
          id: '',
          list_id: '',
        }
        this.showListModal = false
        this.submitForm()
      },
      getListName (listId) {
        const lists = get(this.model, 'data.lists', {})
        return lists[listId] || '- -'
      },
      getNewsLetterName (newsletterId) {
        return this.newsletters.find(n => n.id === newsletterId) || {name: '- -'}
      },
      async getNewsLetters () {
        const {data} = await NewsLetterService.get()
        this.newsletters = data
      },
      async syncThirdPartyData() {
        const { data } = await ThirdPartiesService.getThirdPartyData(this.model.id)
        this.model.data = data.data
      }
    },
    async mounted () {
      if (this.model.name === 'Mailchimp') {
        await this.getNewsLetters()
      }
    },
    watch: {
      data: {
        immediate: true,
        handler (newVal) {
          if (!newVal) {
            return
          }
          this.model = {
            ...newVal
          }
        }
      },
      'model.private_information.key'() {
        if (this.model.name === 'Mailchimp') {
          this.syncThirdPartyData()
        }
      }
    }
  }
</script>
<style>
</style>
